import { Fragment, useCallback, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  Home,
  LineChart,
  ListChecks,
  Package,
  Search,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Loader } from "@/components/ui/Loader";
import { formatCurrency, formatIsoDateBr, formatNumber } from "@/utils/format";
import { cn } from "@/utils/cn";
import { AppropriatedResource } from "@/services/orcamento";
import { OrcamentoFilters } from "@/hooks/useOrcamento";
import {
  InsumoStat,
  pctColor,
  pctDetalheInsumoBarColor,
  pctDetalheInsumoPillClass,
  pctDetalheInsumoTextClass,
  ucLabel,
} from "../chartUtils";

function normalize(s: string): string {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

function codesEquivalent(a: string, b: string): boolean {
  const na = normalize(a);
  const nb = normalize(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const sa = na.replace(/^0+(?=\d)/, "");
  const sb = nb.replace(/^0+(?=\d)/, "");
  if (sa && sb && sa === sb) return true;
  const da = a.replace(/\D/g, "").replace(/^0+/, "");
  const db = b.replace(/\D/g, "").replace(/^0+/, "");
  return da.length >= 4 && db.length >= 4 && da === db;
}

function formatQty(ins: InsumoStat, q: number): string {
  const u = (ins.unit ?? "").trim();
  const n = formatNumber(q, 3);
  return u ? `${n} ${u}` : n;
}

function ResourceThumb({ url, label }: { url?: string; label: string }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) {
    return (
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-100 text-slate-400 sm:h-9 sm:w-9"
        title={label}
        aria-hidden
      >
        <Package className="w-4 h-4" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      className="h-8 w-8 shrink-0 rounded-md border border-slate-200 object-cover bg-white sm:h-9 sm:w-9"
      onError={() => setFailed(true)}
    />
  );
}

/** Documentos de apropriação que compõem o Realizado do insumo × UC. */
function docsForInsumo(rows: AppropriatedResource[], insumo: InsumoStat): AppropriatedResource[] {
  const wantUc = normalize(ucLabel(insumo.ccName));
  const wantId = String(insumo.buildingUnitId ?? "").trim();
  const wantRes = String(insumo.resourceId ?? "").trim();
  const wantDesc = normalize(insumo.description ?? "");

  return rows
    .filter((r) => {
      const sameRes =
        (wantRes !== "" && codesEquivalent(String(r.resourceId ?? ""), wantRes)) ||
        (wantDesc.length >= 6 && (
          normalize(r.description).includes(wantDesc) || wantDesc.includes(normalize(r.description))
        ));
      if (!sameRes) return false;

      if (wantId) {
        const gotId = String(r.buildingUnitId ?? "").trim();
        if (gotId && (gotId === wantId || Number(gotId) === Number(wantId))) return true;
      }
      return normalize(ucLabel(r.buildingUnitName)) === wantUc;
    })
    .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
}

interface GroupRow {
  ucName: string;
  rows: InsumoStat[];
  orcado: number;
  realizado: number;
  saldo: number;
  pct: number;
  qtdOrcada: number;
  qtdRealizada: number;
  saldoQtd: number;
  pctQtd: number;
  groupQtyUnit: string;
}

interface ModalProps {
  insumo: InsumoStat;
  docs: AppropriatedResource[];
  onClose: () => void;
}

function InsumoModal({ insumo, docs, onClose }: ModalProps) {
  const totalQty = docs.reduce((s, r) => s + (r.quantity || 0), 0);
  const totalValue = docs.reduce((s, r) => s + (r.value || 0), 0);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600">
            <Package className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-800 leading-tight truncate">{insumo.description || "Insumo"}</p>
            <p className="text-[10px] text-slate-400 uppercase tracking-wide">Unidade construtiva</p>
            <p className="text-xs text-slate-500 truncate">{insumo.ccName}</p>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <div className="bg-blue-50 rounded-xl px-4 py-3 text-center">
          <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wide">Orcado</p>
          <p className="text-base font-bold text-slate-800 mt-0.5">{formatCurrency(insumo.orcado, false)}</p>
        </div>
        <div className="bg-emerald-50 rounded-xl px-4 py-3 text-center">
          <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide">Realizado</p>
          <p className="text-base font-bold text-slate-800 mt-0.5">{formatCurrency(insumo.realizado, false)}</p>
        </div>
        <div className="bg-slate-50 rounded-xl px-4 py-3 text-center">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Saldo</p>
          <p className="text-base font-bold text-slate-800 mt-0.5">{formatCurrency(insumo.saldo, false)}</p>
        </div>
        <div className="bg-slate-50 rounded-xl px-4 py-3 text-center">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">% Exec.</p>
          <p className="text-base font-bold mt-0.5" style={{ color: pctColor(insumo.pct) }}>{formatNumber(insumo.pct, 1)}%</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-indigo-50 rounded-xl px-3 py-2.5 text-center">
          <p className="text-[9px] font-bold text-indigo-600 uppercase tracking-wide">Qtd orcada</p>
          <p className="text-sm font-bold text-slate-800 mt-0.5 tabular-nums">{formatQty(insumo, insumo.qtdOrcada)}</p>
        </div>
        <div className="bg-teal-50 rounded-xl px-3 py-2.5 text-center">
          <p className="text-[9px] font-bold text-teal-700 uppercase tracking-wide">Qtd realizada</p>
          <p className="text-sm font-bold text-slate-800 mt-0.5 tabular-nums">{formatQty(insumo, insumo.qtdRealizada)}</p>
        </div>
        <div className="bg-slate-50 rounded-xl px-3 py-2.5 text-center">
          <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide">Saldo qtd.</p>
          <p className="text-sm font-bold text-slate-800 mt-0.5 tabular-nums">{formatQty(insumo, insumo.saldoQtd)}</p>
        </div>
        <div className="bg-slate-50 rounded-xl px-3 py-2.5 text-center">
          <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide">% Exec. qtd.</p>
          <p className="text-sm font-bold mt-0.5 tabular-nums" style={{ color: pctColor(insumo.pctQtd) }}>{formatNumber(insumo.pctQtd, 1)}%</p>
        </div>
      </div>

      <div className="flex flex-col gap-1 mb-3">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-400" />
          Documentos de realização
          <span className="text-xs font-normal text-slate-400">
            ({docs.length} registro{docs.length !== 1 ? "s" : ""} neste insumo)
          </span>
        </h3>
        <p className="text-[11px] text-slate-400">
          Movimentos apropriados no orçamento (recebimentos, notas etc.) que compõem o valor Realizado desta unidade construtiva.
        </p>
      </div>

      {docs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-slate-400 gap-2">
          <FileText className="w-10 h-10 opacity-30" />
          <p className="text-sm font-medium">Nenhum documento de realização encontrado para este insumo.</p>
        </div>
      ) : (
        <div className="overflow-auto rounded-xl border border-slate-200" style={{ maxHeight: 360 }}>
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Documento</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Data</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Ref. orçamento</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Qtd</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Valor</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((row, i) => (
                <tr key={`${row.documentLabel}-${row.date}-${i}`} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-2.5 text-xs font-medium text-slate-700" title={row.documentLabel}>
                    {row.documentLabel || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">
                    {row.date ? formatIsoDateBr(row.date) : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600 truncate max-w-[200px]" title={row.costEstimationRef}>
                    {row.costEstimationRef || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs text-slate-700">
                    {row.quantity ? formatNumber(row.quantity, 2) : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs font-semibold text-slate-800">
                    {row.value ? formatCurrency(row.value, false) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 border-t border-slate-200">
              <tr>
                <td colSpan={3} className="px-4 py-2 text-xs font-semibold text-slate-600">
                  Total — documentos (soma das linhas)
                </td>
                <td className="px-4 py-2 text-right text-xs font-semibold text-slate-700">{formatNumber(totalQty, 2)}</td>
                <td className="px-4 py-2 text-right text-xs font-semibold text-slate-800">{formatCurrency(totalValue, false)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Modal>
  );
}

type SortKey = "orcado" | "realizado" | "saldo" | "pct";

interface Props {
  data: InsumoStat[];
  appropriated: AppropriatedResource[];
  loading: boolean;
  applied: OrcamentoFilters | null;
}

export function InsumoDetalheTable({ data, appropriated, loading }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>("orcado");
  const [sortAsc, setSortAsc] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<InsumoStat | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const filteredRows = useMemo(() => {
    const q = normalize(search);
    return (data ?? []).filter((row) => {
      if (!q) return true;
      return normalize(row.description).includes(q) || normalize(row.ccName).includes(q);
    });
  }, [data, search]);

  const groups = useMemo<GroupRow[]>(() => {
    const byUC = new Map<string, InsumoStat[]>();
    for (const row of filteredRows) {
      const key = row.ccName || "Sem Unidade Construtiva";
      if (!byUC.has(key)) byUC.set(key, []);
      byUC.get(key)!.push(row);
    }

    const out: GroupRow[] = Array.from(byUC.entries()).map(([ucName, rows]) => {
      const sortedRows = [...rows].sort((a, b) => b.orcado - a.orcado);
      const orcado = sortedRows.reduce((s, r) => s + r.orcado, 0);
      const realizado = sortedRows.reduce((s, r) => s + r.realizado, 0);
      const saldo = orcado - realizado;
      const pct = orcado > 0 ? (realizado / orcado) * 100 : 0;
      const qtdOrcada = sortedRows.reduce((s, r) => s + r.qtdOrcada, 0);
      const qtdRealizada = sortedRows.reduce((s, r) => s + r.qtdRealizada, 0);
      const saldoQtd = qtdOrcada - qtdRealizada;
      const pctQtd = qtdOrcada > 0 ? (qtdRealizada / qtdOrcada) * 100 : 0;
      const u0 = (sortedRows[0]?.unit ?? "").trim();
      const sameUnit = u0 !== "" && sortedRows.every((r) => (r.unit ?? "").trim() === u0);
      return {
        ucName,
        rows: sortedRows,
        orcado,
        realizado,
        saldo,
        pct,
        qtdOrcada,
        qtdRealizada,
        saldoQtd,
        pctQtd,
        groupQtyUnit: sameUnit ? u0 : "",
      };
    });

    out.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const cmp = typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv), "pt-BR");
      return sortAsc ? cmp : -cmp;
    });

    return out;
  }, [filteredRows, sortAsc, sortKey]);

  const visibleInsumosCount = useMemo(
    () => groups.reduce((acc, g) => acc + (expanded[g.ucName] ? g.rows.length : 0), 0),
    [groups, expanded],
  );

  const handleVerTodos = useCallback(() => {
    setSearch("");
    setExpanded((prev) => {
      const next = { ...prev };
      for (const g of groups) next[g.ucName] = true;
      return next;
    });
  }, [groups]);

  const summary = useMemo(() => {
    const orcado = filteredRows.reduce((s, r) => s + r.orcado, 0);
    const realizado = filteredRows.reduce((s, r) => s + r.realizado, 0);
    const saldo = orcado - realizado;
    const pct = orcado > 0 ? (realizado / orcado) * 100 : 0;
    return { orcado, realizado, saldo, pct };
  }, [filteredRows]);

  const selectedDocs = useMemo(
    () => (selected ? docsForInsumo(appropriated ?? [], selected) : []),
    [appropriated, selected],
  );

  function toggle(ucName: string) {
    setExpanded((prev) => ({ ...prev, [ucName]: !prev[ucName] }));
  }

  function sortBy(key: SortKey) {
    if (sortKey === key) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(false);
    }
  }

  return (
    <>
      <div className="min-w-0 rounded-2xl border border-slate-200/90 bg-slate-50/50 p-4 sm:p-5 shadow-sm flex flex-col gap-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-[22px] font-bold text-slate-900 leading-tight tracking-tight">Detalhamento por Insumo</h3>
            <p className="text-sm text-slate-500 mt-1">
              {filteredRows.length} insumos <span className="text-slate-400">•</span> clique para expandir por unidade construtiva
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Buscar insumo ou UC..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-800 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-400"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <FileText className="w-6 h-6" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Orçado</p>
              <div className="mt-1 flex flex-wrap items-baseline gap-2">
                <p className="text-2xl font-bold text-slate-900 leading-tight">{formatCurrency(summary.orcado, true)}</p>
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">100%</span>
              </div>
            </div>
          </div>
          <div className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <ListChecks className="w-6 h-6" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Realizado</p>
              <p className="mt-1 text-2xl font-bold text-slate-900 leading-tight">{formatCurrency(summary.realizado, true)}</p>
            </div>
          </div>
          <div className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <Clock className="w-6 h-6" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Saldo</p>
              <div className="mt-1 flex flex-wrap items-baseline gap-2">
                <p className={cn("text-2xl font-bold leading-tight", summary.saldo < 0 ? "text-red-600" : "text-slate-900")}>
                  {formatCurrency(summary.saldo, true)}
                </p>
                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                  {formatNumber(summary.orcado > 0 ? (summary.saldo / summary.orcado) * 100 : 0, 1)}%
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600">
              <LineChart className="w-6 h-6" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">% Realizado</p>
              <div className="mt-1 flex flex-wrap items-baseline gap-2">
                <p className={cn("text-2xl font-bold leading-tight", pctDetalheInsumoTextClass(summary.pct))}>
                  {formatNumber(summary.pct, 1)}%
                </p>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">exec.</span>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40 rounded-xl border border-slate-200 bg-white">
            <Loader size="md" label="Carregando dados..." />
          </div>
        ) : groups.length === 0 ? (
          <div className="flex items-center justify-center h-32 rounded-xl border border-dashed border-slate-200 bg-white text-slate-400 text-sm">
            Nenhum insumo encontrado.
          </div>
        ) : (
          <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="max-h-[min(520px,70vh)] overflow-y-auto overflow-x-hidden">
              <table className="w-full table-fixed border-collapse text-xs sm:text-sm">
                <colgroup>
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "6%" }} />
                </colgroup>
                <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 backdrop-blur-sm">
                  <tr>
                    <th className="min-w-0 px-2 py-2.5 text-left text-[9px] font-bold uppercase leading-tight tracking-wide text-slate-500 sm:px-3 sm:text-[10px] sm:tracking-wider">Insumo / Unidade</th>
                    <th className="min-w-0 px-1.5 py-2.5 text-right text-[9px] font-bold uppercase leading-tight text-slate-500 cursor-pointer sm:px-2 sm:text-[10px]" onClick={() => sortBy("orcado")}>Orçado</th>
                    <th className="min-w-0 px-1.5 py-2.5 text-right text-[9px] font-bold uppercase leading-tight text-slate-500 cursor-pointer sm:px-2 sm:text-[10px]" onClick={() => sortBy("realizado")}>Realizado</th>
                    <th className="min-w-0 px-1.5 py-2.5 text-right text-[9px] font-bold uppercase leading-tight text-slate-500 cursor-pointer sm:px-2 sm:text-[10px]" onClick={() => sortBy("saldo")}>Saldo</th>
                    <th className="min-w-0 px-1.5 py-2.5 text-right text-[9px] font-bold uppercase leading-tight text-slate-500 cursor-pointer sm:px-2 sm:text-[10px]" onClick={() => sortBy("pct")}>% Exec.</th>
                    <th className="min-w-0 px-1.5 py-2.5 text-right text-[9px] font-bold uppercase leading-tight text-slate-500 sm:px-2 sm:text-[10px]">Qtd orç.</th>
                    <th className="min-w-0 px-1.5 py-2.5 text-right text-[9px] font-bold uppercase leading-tight text-slate-500 sm:px-2 sm:text-[10px]">Qtd realiz.</th>
                    <th className="min-w-0 px-1.5 py-2.5 text-right text-[9px] font-bold uppercase leading-tight text-slate-500 sm:px-2 sm:text-[10px]">Saldo qtd.</th>
                    <th className="min-w-0 px-1 py-2.5 text-right text-[9px] font-bold uppercase leading-tight text-slate-500 sm:px-2 sm:text-[10px]">%</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group) => {
                    const isOpen = Boolean(expanded[group.ucName]);
                    return (
                      <Fragment key={`group-wrap-${group.ucName}`}>
                        <tr key={`group-${group.ucName}`} className="border-t border-sky-100 bg-sky-50/80 hover:bg-sky-50">
                          <td className="min-w-0 px-2 py-2.5 sm:px-3 sm:py-3">
                            <button type="button" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-left w-full min-w-0" onClick={() => toggle(group.ucName)}>
                              {isOpen ? <ChevronDown className="w-4 h-4 text-sky-700 shrink-0" /> : <ChevronRight className="w-4 h-4 text-sky-700 shrink-0" />}
                              <Home className="w-4 h-4 text-sky-600 shrink-0" />
                              <span className="min-w-0 break-words text-[11px] font-bold text-sky-950 sm:text-xs">{group.ucName}</span>
                              <span className="text-[10px] font-medium text-sky-700/80">({group.rows.length} insumos)</span>
                            </button>
                          </td>
                          <td className="min-w-0 px-1.5 py-2.5 text-right text-[11px] font-bold tabular-nums text-slate-800 leading-tight break-words sm:px-2 sm:py-3 sm:text-xs">{formatCurrency(group.orcado, true)}</td>
                          <td className="min-w-0 px-1.5 py-2.5 text-right text-[11px] font-bold tabular-nums text-slate-800 leading-tight break-words sm:px-2 sm:py-3 sm:text-xs">{formatCurrency(group.realizado, true)}</td>
                          <td className={cn("min-w-0 px-1.5 py-2.5 text-right text-[11px] font-bold tabular-nums leading-tight break-words sm:px-2 sm:py-3 sm:text-xs", group.saldo < 0 ? "text-red-600" : "text-slate-800")}>{formatCurrency(group.saldo, true)}</td>
                          <td className="min-w-0 px-1.5 py-2.5 sm:px-2 sm:py-3">
                            <div className="flex min-w-0 items-center justify-end gap-1">
                              <div className="h-1.5 min-w-0 max-w-[3.5rem] flex-1 rounded-full bg-slate-200 overflow-hidden sm:max-w-[4.5rem]">
                                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, group.pct)}%`, background: pctDetalheInsumoBarColor(group.pct) }} />
                              </div>
                              <span className={cn("shrink-0 text-[10px] font-bold tabular-nums sm:text-xs", pctDetalheInsumoTextClass(group.pct))}>{formatNumber(group.pct, 1)}%</span>
                            </div>
                          </td>
                          <td className="min-w-0 px-1.5 py-2.5 text-right text-[10px] font-bold tabular-nums text-slate-800 leading-tight break-words sm:px-2 sm:py-3 sm:text-xs">
                            {group.groupQtyUnit ? `${formatNumber(group.qtdOrcada, 3)} ${group.groupQtyUnit}` : "—"}
                          </td>
                          <td className="min-w-0 px-1.5 py-2.5 text-right text-[10px] font-bold tabular-nums text-slate-800 leading-tight break-words sm:px-2 sm:py-3 sm:text-xs">
                            {group.groupQtyUnit ? `${formatNumber(group.qtdRealizada, 3)} ${group.groupQtyUnit}` : "—"}
                          </td>
                          <td className={cn("min-w-0 px-1.5 py-2.5 text-right text-[10px] font-bold tabular-nums leading-tight break-words sm:px-2 sm:py-3 sm:text-xs", group.groupQtyUnit && group.saldoQtd < 0 ? "text-red-600" : "text-slate-800")}>
                            {group.groupQtyUnit ? `${formatNumber(group.saldoQtd, 3)} ${group.groupQtyUnit}` : "—"}
                          </td>
                          <td className="min-w-0 px-1 py-2.5 text-right sm:px-2 sm:py-3">
                            {group.groupQtyUnit ? (
                              <span className={cn("inline-flex min-w-[2.25rem] justify-center rounded-full px-2 py-0.5 text-[10px] font-bold", pctDetalheInsumoPillClass(group.pctQtd))}>
                                {Math.round(group.pctQtd)}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                        {isOpen && group.rows.map((row, idx) => (
                          <tr
                            key={`${group.ucName}-${idx}-${row.resourceId}`}
                            className="border-t border-slate-100 bg-white hover:bg-slate-50/80 cursor-pointer transition-colors"
                            onClick={() => setSelected(row)}
                            title="Clique para ver documentos de realização"
                          >
                            <td className="min-w-0 px-2 py-2.5 pl-4 sm:px-3 sm:py-3 sm:pl-6">
                              <div className="flex min-w-0 items-start gap-2 sm:gap-3">
                                <ResourceThumb url={row.imageUrl} label={row.description || row.resourceId} />
                                <div className="min-w-0 flex-1">
                                  <p className="break-words text-[11px] font-semibold leading-snug text-slate-800 line-clamp-3 sm:text-xs" title={row.description}>{row.description || "—"}</p>
                                  {row.unit ? <p className="mt-0.5 text-[9px] text-slate-500 sm:text-[10px]">Un.: {row.unit}</p> : null}
                                </div>
                              </div>
                            </td>
                            <td className="min-w-0 px-1.5 py-2.5 text-right text-[11px] font-medium tabular-nums text-slate-800 leading-tight break-words sm:px-2 sm:py-3 sm:text-xs">{formatCurrency(row.orcado, true)}</td>
                            <td className="min-w-0 px-1.5 py-2.5 text-right text-[11px] font-medium tabular-nums text-slate-800 leading-tight break-words sm:px-2 sm:py-3 sm:text-xs">{formatCurrency(row.realizado, true)}</td>
                            <td className={cn("min-w-0 px-1.5 py-2.5 text-right text-[11px] font-medium tabular-nums leading-tight break-words sm:px-2 sm:py-3 sm:text-xs", row.saldo < 0 ? "text-red-600" : "text-slate-800")}>{formatCurrency(row.saldo, true)}</td>
                            <td className="min-w-0 px-1.5 py-2.5 sm:px-2 sm:py-3">
                              <div className="flex min-w-0 items-center justify-end gap-1">
                                <div className="h-1.5 min-w-0 max-w-[3rem] flex-1 rounded-full bg-slate-200 overflow-hidden sm:max-w-[4rem]">
                                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, row.pct)}%`, background: pctDetalheInsumoBarColor(row.pct) }} />
                                </div>
                                <span className={cn("shrink-0 text-[10px] font-bold tabular-nums sm:text-xs", pctDetalheInsumoTextClass(row.pct))}>{formatNumber(row.pct, 1)}%</span>
                              </div>
                            </td>
                            <td className="min-w-0 px-1.5 py-2.5 text-right text-[10px] tabular-nums text-slate-700 leading-tight break-words sm:px-2 sm:py-3 sm:text-xs">{formatQty(row, row.qtdOrcada)}</td>
                            <td className="min-w-0 px-1.5 py-2.5 text-right text-[10px] tabular-nums text-slate-700 leading-tight break-words sm:px-2 sm:py-3 sm:text-xs">{formatQty(row, row.qtdRealizada)}</td>
                            <td className={cn("min-w-0 px-1.5 py-2.5 text-right text-[10px] tabular-nums leading-tight break-words sm:px-2 sm:py-3 sm:text-xs", row.saldoQtd < 0 ? "font-semibold text-red-600" : "text-slate-700")}>{formatQty(row, row.saldoQtd)}</td>
                            <td className="min-w-0 px-1 py-2.5 text-right sm:px-2 sm:py-3">
                              <span className={cn("inline-flex min-w-[2.25rem] justify-center rounded-full px-2 py-0.5 text-[10px] font-bold", pctDetalheInsumoPillClass(row.pctQtd))}>
                                {Math.round(row.pctQtd)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-4 border-t border-slate-200 bg-slate-50/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-[10px] leading-tight text-slate-600">
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 shrink-0 rounded-full bg-slate-300" />0% Não iniciado</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />0% – 90% Dentro do previsto</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 shrink-0 rounded-full bg-amber-400" />90% – 110% Atenção</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 shrink-0 rounded-full bg-red-500" />&gt; 110% Acima do previsto</span>
              </div>
              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-4">
                <span className="text-center text-xs text-slate-500 sm:text-right">
                  Exibindo {visibleInsumosCount} de {filteredRows.length} insumos
                </span>
                <button
                  type="button"
                  onClick={handleVerTodos}
                  className="rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-600 shadow-sm transition-colors hover:bg-blue-50"
                >
                  Ver todos
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <InsumoModal
          insumo={selected}
          docs={selectedDocs}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
